import { useEffect, useState } from 'react';

import {
  getStorageItem,
  removeStorageItem,
  setStorageItem,
} from '../helpers/localStorage';

const isBrowser = typeof window !== 'undefined';

/**
 * Description:
 * Custom React hook for managing localStorage with type safety.
 */
export const useLocalStorage = <T = string>(
  key: string,
  initialValue?: T,
): [T, (value: T, stringify?: boolean) => void, () => void] => {
  if (!key) {
    throw new Error('useLocalStorage key may not be falsy');
  }

  const readValue = (): T | undefined => {
    if (!isBrowser) {
      return initialValue;
    }

    try {
      const item = getStorageItem(key) as T | undefined;
      return item ?? initialValue;
    } catch (error) {
      console.error(`Error reading localStorage: [${key}]: ${error}`);
      return initialValue;
    }
  };

  // Remove the state and clear the localStorage item
  const removeValue = () => {
    try {
      setStoredValue(initialValue);
      if (isBrowser) {
        removeStorageItem(key);
      }
    } catch (error) {
      console.error(`Error removing localStorage: [${key}]: ${error}`);
    }
  };

  // Initialize the state with the value from localStorage or the initial value
  const [storedValue, setStoredValue] = useState<T | undefined>(readValue);

  // Function to update the state and the localStorage value
  const setValue = (value: T | undefined, stringify = true) => {
    try {
      setStoredValue(value);
      if (isBrowser) {
        if (value === undefined) {
          removeStorageItem(key);
        } else {
          setStorageItem(key, value, stringify);
        }
      }
    } catch (error) {
      console.error(`Error setting localStorage: [${key}]: ${error}`);
    }
  };

  // Sync state with localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      setStoredValue(readValue());
    };

    if (isBrowser) {
      window.addEventListener('storage', handleStorageChange);

      return () => {
        window.removeEventListener('storage', handleStorageChange);
      };
    }
  }, [key]);

  return [storedValue, setValue, removeValue];
};
